import {
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    inject,
    signal,
    viewChild,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CODE_LENGTH } from '../../constant/code.constant';

@Component({
    standalone: false,
    selector: 'app-code-input',
    templateUrl: './code-input.component.html',
})
export class CodeInputComponent implements OnInit {
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);

    @Input({ required: true }) control!: AbstractControl;
    @Input({ required: true }) inputId!: string;
    @Input({ required: true }) testId!: string;
    @Input() describedBy: string | null = null;
    @Input() autoSubmit = false;
    @Output() completed = new EventEmitter<string>();

    protected readonly isFocused = signal(false);

    private readonly codeInput = viewChild.required<ElementRef<HTMLInputElement>>('codeInput');

    ngOnInit(): void {
        this.control.events
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.cdr.markForCheck());
    }

    get code(): string {
        return this.control.value ?? '';
    }

    get codeSlots(): string[] {
        return Array.from({ length: CODE_LENGTH }, (_, index) => this.code[index] ?? '');
    }

    get activeSlot(): number {
        return Math.min(this.code.length, CODE_LENGTH - 1);
    }

    get isInvalid(): boolean {
        return this.control.invalid && this.control.touched;
    }

    focus(): void {
        this.codeInput().nativeElement.focus();
    }

    protected onInput(): void {
        const input = this.codeInput().nativeElement;
        const digits = input.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
        input.value = digits;
        this.control.setValue(digits);
        if (this.autoSubmit && digits.length === CODE_LENGTH) this.completed.emit(digits);
    }

    protected moveCaretToEnd(): void {
        const input = this.codeInput().nativeElement;
        input.setSelectionRange(input.value.length, input.value.length);
    }

    protected onBlur(): void {
        this.isFocused.set(false);
        this.control.markAsTouched();
    }
}
